// Compact (mobile-style) record list — the card-stack peer of ListView. Pure
// presentation: every bit of data and every action is injected by the host
// (TxnScreen / the embed capture preview), so it reuses the exact same list
// state, filters, actions and pagination the desktop grid consumes. A port of
// the Android transaction list: a scrollable stack of record cards, a header
// with a client-side search toggle + (when the design enforces them) a filter
// sheet, and a gradient Add FAB.

import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Eye, History, Pencil, Play, Plus, RefreshCcw, Search, SlidersHorizontal, Tags, Trash2, UserPlus, Waypoints, X,
} from 'lucide-react'
import type { ActionDef, SqlModel } from '../types'
import type {
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice,
} from '../contracts'
import { openRecord, rowEditable, rowHiddenActions, rowLockReason, visibleRowActions } from '../utils'
import RowActionsMenu from '../list/RowActionsMenu'
import PaginationBar, { type ListPagination } from '../list/PaginationBar'
import EnforcedFilterControls, { type OverrideMap } from '../list/EnforcedFilterControls'
import { feedViewColumns } from '../flow/feedLayout'
import CompactRecordCard from './CompactRecordCard'
import { compactListColumns, compactCellValue } from './heuristics'

interface RowShape extends Record<string, unknown> {
  _txn_id: string
}

interface Props {
  model: SqlModel
  rows: RowShape[]
  total: number
  busy: boolean
  /** Draw the object's name at the head of the header bar. Defaults TRUE; the
   *  host passes false when the surrounding shell already names this object.
   *  Same contract as ListView — see ../../objectChrome. */
  showTitle?: boolean
  canAdd?: boolean
  canDelete?: boolean
  onAdd: () => void
  // Open a record — the host routes an editable row to edit and a locked row to
  // a read-only view. `opts.view` is a hint (true when the row is not editable).
  onOpen: (txnId: string, opts?: { view?: boolean }) => void
  // Tapping a card opens it read-only (Vision's browse screen); only the
  // card's Edit menu entry opens it for editing.
  cardOpensView?: boolean
  onDelete: (txnId: string) => void
  onHistory?: (txnId: string) => void
  // This record's chain across the objects it is linked to. Set only when the
  // object is placed on a process flow — see ListView.
  onProcessTrail?: (txnId: string) => void
  // "Also known as" — present only for a vector-searchable master that allows
  // aliases (see TxnScreen), so it never appears where it would do nothing.
  onAlias?: (txnId: string) => void
  // "Assign to…" — hand this record to a person or a role (see ListView).
  onAssign?: (txnId: string) => void
  onRefresh: () => void
  // Form-scoped custom actions (rendered in the header) and row-scoped ones
  // (added to each card's ⋮ menu). The host owns execution.
  formActions?: ActionDef[]
  rowActions?: ActionDef[]
  onRunFormAction?: (name: string) => void
  onRunRowAction?: (name: string, txnId: string) => void
  // System add / edit / view overrides relabel the built-in affordances and
  // route the click through the action runner so pre-action validations run.
  systemOverrides?: { add?: ActionDef; edit?: ActionDef; view?: ActionDef; delete?: ActionDef }
  // Designer-enforced conditions + filter sets — a filter sheet is offered only
  // when one of these is present.
  enforcedFilters?: EnforcedConditionEcho[]
  onApplyFilters?: (overrides: OverrideMap) => void
  filterSets?: EnforcedFilterSetEcho[]
  activeFilterSetId?: string | null
  bypassed?: boolean
  onSelectFilterSet?: (sel: { setId?: string; bypass?: boolean }) => void
  // Non-blocking messages from the conditions' validations.
  filterNotices?: FilterNotice[]
  // Only the filter sheet needs these — to open a condition's reference picker.
  project?: string
  objName?: string
  // Server-side pagination — the SAME object ListView receives, drawn by the
  // SAME <PaginationBar>. A card stack is a different way of reading one page
  // of records, not a different page of them: rows-per-page, Continuous ⇆
  // Pages and Prev/Next all belong here too, and the lone "Load more" button
  // this used to carry meant the compact view could only ever reach the first
  // page it was given.
  pagination?: ListPagination
  // Interface switcher node (List / Spread sheet / Form input / Compact).
  viewSwitcher?: ReactNode
  // Extra header controls (e.g. the Approvals inbox button) shown before Add.
  beforeAddActions?: ReactNode
  // Host chrome (e.g. the designer deploy menu).
  headerActions?: ReactNode
  // When set, a card tap invokes onSelect (bind selection vars) instead of
  // opening the editor — smart-page embeds use this.
  onSelect?: (row: RowShape) => void
}

export default function CompactListView({
  model, rows, total, busy, showTitle = true,
  canAdd = true, canDelete = true,
  onAdd, onOpen, cardOpensView = false, onDelete, onHistory, onProcessTrail, onAlias, onAssign, onRefresh,
  formActions, rowActions, onRunFormAction, onRunRowAction, systemOverrides,
  enforcedFilters, onApplyFilters, filterSets, activeFilterSetId, bypassed, onSelectFilterSet,
  filterNotices, project, objName,
  pagination, viewSwitcher, beforeAddActions, headerActions, onSelect,
}: Props) {
  const [searchOpen, setSearchOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)

  const addOverride = onRunFormAction ? systemOverrides?.add : undefined
  const editOverride = onRunRowAction ? systemOverrides?.edit : undefined
  const viewOverride = onRunRowAction ? systemOverrides?.view : undefined
  const addLabel = addOverride?.name || 'Add'

  const hasFilters = (enforcedFilters?.length || 0) > 0 || (filterSets?.length || 0) > 0

  // Client-side search over the card columns' values (matches the Android
  // list's in-memory substring filter). Search what the card SHOWS: with a feed
  // view, that is the feed's columns, not the positional defaults.
  const cardCols = useMemo(() => {
    const feed = feedViewColumns(model)
    return feed.length ? feed : compactListColumns(model)
  }, [model])
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(r =>
      cardCols.some(c => (compactCellValue(r, c) || '').toLowerCase().includes(q)),
    )
  }, [rows, search, cardCols])

  const doAdd = () => {
    if (busy || !canAdd) return
    if (addOverride) onRunFormAction!(addOverride.name)
    else onAdd()
  }

  /** Every way of opening a record here routes through the shared gate, so a
   *  card click obeys the same edit/view conditions the list's pencil does. */
  const openRow = (row: RowShape, view = false) => openRecord({
    txnId: row._txn_id,
    editable: !view && rowEditable(row),
    systemOverrides: { edit: editOverride, view: viewOverride },
    onRunRowAction,
    onOpen,
  })

  const openCard = (row: RowShape) => {
    if (onSelect) { onSelect(row); return }
    openRow(row, cardOpensView)
  }

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 14px', borderBottom: '1px solid var(--color-border)',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flex: 1, minWidth: 0 }}>
          {showTitle && (
            <strong style={{ fontSize: '0.9625rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {model.form_title || model.main_table}
            </strong>
          )}
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            {visibleRows.length === total
              ? `${total} record${total === 1 ? '' : 's'}`
              : `${visibleRows.length} of ${total}`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setSearchOpen(o => { if (o) setSearch(''); return !o }) }}
            title={searchOpen ? 'Close search' : 'Search'}
            style={{ padding: 5 }}
          >
            {searchOpen ? <X size={15} /> : <Search size={15} />}
          </button>
          {hasFilters && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFilterOpen(true)}
              title="Filters"
              style={{ padding: 5, color: bypassed ? undefined : 'var(--color-accent, #4c8dff)' }}
            >
              <SlidersHorizontal size={15} />
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onRefresh} disabled={busy} title="Refresh" style={{ padding: 5 }}>
            <RefreshCcw size={15} />
          </button>
          {beforeAddActions}
          {(formActions || []).map(a => (
            <button
              key={`fa-${a.name}`}
              className="btn btn-ghost btn-sm"
              onClick={() => onRunFormAction?.(a.name)}
              disabled={busy}
              title={a.name}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Play size={13} /> {a.name}
            </button>
          ))}
          {viewSwitcher}
          {headerActions}
        </div>
      </div>

      {searchOpen && (
        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--color-border)' }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search loaded records…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', fontSize: '0.9625rem',
              background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 8,
              outline: 'none',
            }}
          />
        </div>
      )}

      {/* Card stack */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 14 }}>
        {visibleRows.length === 0 ? (
          <div style={{
            padding: 40, textAlign: 'center',
            color: 'var(--color-text-secondary)', fontSize: '0.9625rem',
          }}>
            {rows.length === 0
              ? <>No records yet. Tap <strong>{addLabel}</strong> to create one.</>
              : 'No records match the current search / filters.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: canAdd ? 76 : 0 }}>
            {visibleRows.map(row => (
              <CompactRecordCard
                key={row._txn_id}
                sm={model}
                row={row}
                onClick={() => openCard(row)}
                trailing={
                  <RowActionsMenu
                    actions={[
                      {
                        label: rowEditable(row) ? (editOverride?.name || 'Edit') : (rowLockReason(row) || 'View'),
                        icon: rowEditable(row) ? <Pencil size={14} /> : <Eye size={14} />,
                        onClick: () => openRow(row),
                      },
                      // Only the actions the engine did not hide for THIS row.
                      ...visibleRowActions(rowActions, rowHiddenActions(row)).map(a => ({
                        label: a.name,
                        icon: <Play size={14} />,
                        onClick: () => onRunRowAction?.(a.name, row._txn_id),
                      })),
                      ...(onAssign ? [{ label: 'Assign to\u2026', icon: <UserPlus size={14} />, onClick: () => onAssign(row._txn_id) }] : []),
                      ...(onAlias ? [{ label: 'Also known as', icon: <Tags size={14} />, onClick: () => onAlias(row._txn_id) }] : []),
                      ...(onProcessTrail ? [{ label: 'Process trail', icon: <Waypoints size={14} />, onClick: () => onProcessTrail(row._txn_id) }] : []),
                      ...(onHistory ? [{ label: 'Audit trail', icon: <History size={14} />, onClick: () => onHistory(row._txn_id) }] : []),
                      ...(canDelete && (!systemOverrides?.delete || visibleRowActions([systemOverrides.delete], rowHiddenActions(row)).length) ? [{ label: systemOverrides?.delete?.name || 'Delete', icon: <Trash2 size={14} />, onClick: () => onDelete(row._txn_id), danger: true }] : []),
                    ]}
                  />
                }
              />
            ))}
          </div>
        )}
      </div>

      {pagination && (
        <PaginationBar
          pagination={pagination}
          busy={busy}
          hasRows={visibleRows.length > 0}
        />
      )}

      {/* Gradient Add FAB */}
      {canAdd && (
        <button
          onClick={doAdd}
          disabled={busy}
          title={addOverride?.name || addLabel}
          aria-label={addLabel}
          style={{
            // Above the pagination footer when there is one — the FAB floats
            // over this container, and a 56px circle sitting on the bar makes
            // Rows-per-page unreachable on exactly the narrow screens this
            // view is for.
            position: 'absolute', right: 18, bottom: pagination ? 62 : 18,
            width: 56, height: 56, borderRadius: 28,
            border: 'none', cursor: busy ? 'wait' : 'pointer',
            background: 'linear-gradient(135deg, var(--color-accent, #4c8dff), var(--color-accent-strong, var(--color-accent-hvr, #3a7bff)))',
            color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
            opacity: busy ? 0.6 : 1,
          }}
        >
          <Plus size={26} />
        </button>
      )}

      {/* Filter sheet (bottom overlay) — only when the design enforces filters */}
      {filterOpen && hasFilters && (
        <div
          onClick={() => setFilterOpen(false)}
          style={{ position: 'absolute', inset: 0, zIndex: 30, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-end' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxHeight: '80%', overflow: 'auto',
              background: 'var(--color-bg)',
              borderTopLeftRadius: 16, borderTopRightRadius: 16,
              boxShadow: '0 -12px 40px rgba(0,0,0,0.3)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
            }}>
              <strong style={{ fontSize: '0.9625rem' }}>Filters</strong>
              <button className="btn btn-ghost btn-sm" onClick={() => setFilterOpen(false)} style={{ padding: 4 }}>
                <X size={16} />
              </button>
            </div>
            <EnforcedFilterControls
              model={model}
              enforcedFilters={enforcedFilters}
              filterSets={filterSets}
              activeFilterSetId={activeFilterSetId}
              bypassed={bypassed}
              busy={busy}
              notices={filterNotices}
              project={project}
              objName={objName}
              onApplyFilters={(o) => { onApplyFilters?.(o); setFilterOpen(false) }}
              onSelectFilterSet={(s) => { onSelectFilterSet?.(s); setFilterOpen(false) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
